import { computed, DestroyRef, inject, Injector, signal, type Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, type FormControlStatus } from '@angular/forms';

import { startWith, switchMap } from 'rxjs';

export const formSignalValue = <T extends AbstractControl | Signal<AbstractControl>>(
  control: T,
): Signal<
  T extends AbstractControl
    ? ReturnType<T['getRawValue']>
    : T extends Signal<AbstractControl>
      ? ReturnType<ReturnType<T>['getRawValue']>
      : never
> => {
  if (control instanceof AbstractControl) {
    return toSignal(control.valueChanges, {
      requireSync: false,
      initialValue: control.getRawValue(),
    });
  } else {
    const injector = inject(Injector);
    const destroyRef = inject(DestroyRef);
    /**
     * If control is passed as signal input required
     * we cant read its value in the constructor context
     * because it is not yet initialized.
     */
    const formVal = signal<any | null>(null);
    toObservable(control, { injector })
      .pipe(
        switchMap(controlVal => controlVal.valueChanges.pipe(startWith(controlVal.getRawValue()))),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(value => formVal.set(value));
    return computed(() => formVal()!);
  }
};

export const formSignalStatus = <T extends AbstractControl>(
  control: T,
): Signal<FormControlStatus> =>
  toSignal(control.statusChanges, {
    requireSync: false,
    initialValue: control.status,
  });
